export default class {
  constructor(options = {}) {
    this._websocket = null
    this.error_callback = options.error_callback || this.__onError
    this.evalute_callback = options.evalute_callback || this.__onEvalute
    this.state = null
    this.promisePool = []
    this.timeout = 10000
    this.timeoutObj = null
    this.lockReconnect = false
    this.closeConfig = {
      closing: false,
    }
  }
  /**
   * 建立评测连接。
   *
   * 与旧实现的三点区别，都是为了修掉线上那个「网络不稳定」：
   *
   * 1. 不再复用 localStorage 里的签名地址。讯飞的鉴权信息就写在 URL 的 date/authorization
   *    上，与服务端时间相差 5 分钟以上握手直接失败；而旧代码把地址长期缓存、只在 onerror
   *    里清除，于是「签发满 5 分钟后的第一次录音」必然失败一次，用户看到黑色 toast、再点
   *    一次才好，一场十几道题的测试会撞上三四次。线上抓到的现场：
   *      wss://ise-api.xfyun.cn/v2/open-ise?...&date=Thu, 10 Sep 2026 06:09:47 GMT → failed
   *      CloseEvent { code: 1006, wasClean: false }
   *    时效性凭据不做缓存，每次现签现用。（顺带：record() 里的 postEventAiData 打点本来
   *    就在请求同一个接口，所以请求数量并没有真的增加。）
   *
   * 2. 握手失败自动重签一次再连，用户无感；两次都失败才提示。
   *
   * 3. 握手阶段的 onerror / onclose 不再各自调一次 error_callback（旧实现因此弹两条
   *    toast、控制台打两条「评测失败」），统一由本 Promise 落地一次。
   */
  open() {
    return new Promise((resolve, reject) => {
      if (!('WebSocket' in window)) {
        reject({
          data: 'Your browser does not support WebSocket!',
        })
        return
      }
      this.__connect()
        .then(resolve)
        .catch(() => {
          // 握手失败最常见的原因就是地址过期，重签一次再试
          this.__connect()
            .then(resolve)
            .catch((error) => {
              this.error_callback(error)
              reject(error)
            })
        })
    })
  }
  /**
   * 取一次新地址并建立连接。
   * 返回的 Promise 只反映「握手」结果：连上 resolve，握手失败 reject；
   * 连接建立之后的错误（服务端错误码、异常断开）照旧走 error_callback。
   */
  __connect() {
    return new Promise((resolve, reject) => {
      this.__fetchUrl()
        .then((aiUrl) => {
          const ws = new WebSocket(aiUrl)
          this._websocket = ws
          // 握手期间只允许落地一次：onerror 与 onclose 会连着来两次
          let opened = false
          let settled = false
          const failHandshake = (e) => {
            if (settled) return
            settled = true
            this._websocket = null
            reject(e)
          }
          ws.onopen = (e) => {
            opened = true
            settled = true
            resolve(e)
          }
          ws.onerror = (e) => {
            console.error('[ise] websocket error', e)
            if (!opened) {
              failHandshake(e)
              return
            }
            this.error_callback(e)
          }
          ws.onclose = (e) => {
            this._websocket = null
            if (!opened) {
              failHandshake(e)
              return
            }
            if (!this.closeConfig.closing) {
              console.error('[ise] websocket 被关闭', e.code, e.reason)
              this.error_callback(e)
            }
            // 若手动close，恢复初始状态
            this.closeConfig.closing = false
          }
          ws.onmessage = (e) => {
            const data = JSON.parse(e.data)
            if (data.code == 0) {
              if (data.data.status == 2) {
                this.__close()
              }
              this.evalute_callback(data)
            } else {
              // 打出错误码：组件侧「Try again」分支不会上报，此前线上从来看不到具体原因
              console.error('[ise] 评测服务返回错误码', data.code, data.message || '', data)
              this.__close()
              this.error_callback(data)
            }
          }
        })
        .catch(reject)
    })
  }
  send(data) {
    // return new Promise((resolve, reject) => {
    //     this.promisePool[data.business.guid] = {
    //       resolve,
    //       reject
    //     };

    // })
    if (this._websocket && this._websocket.readyState === 1) {
      this._websocket.send(JSON.stringify(data))
    }
  }
  reconnect() {
    if (this.lockReconnect) return
    // this._websocket = null
    this.lockReconnect = true
    setTimeout(() => {
      this.open()
      this.lockReconnect = false
    }, 2000)
  }
  close() {
    console.log('close')
    if (this._websocket && (this._websocket.readyState === 0 || this._websocket.readyState === 1)) {
      this.closeConfig.closing = true
      this._websocket.close()
    }
  }
  __close() {
    const ws = this._websocket
    if (!ws || ws.readyState !== 1) return
    this.closeConfig.closing = true
    // 先摘掉引用和回调再 close()：close() 一旦同步触发 onclose（部分实现会），
    // 原来的写法会在 onclose 里把 _websocket 置空之后再去读它，直接空指针
    this._websocket = null
    ws.onclose = null
    ws.close()
    // onclose 已被摘掉，没人再复位这个标志。就地复位，否则同一实例下次被服务端
    // 关闭时 onclose 会被误判成「主动关闭」而吞掉错误（retry 会复用同一实例）
    this.closeConfig.closing = false
  }
  __onError(e) {
    console.log(e)
  }
  __onEvalute(e) {
    console.log(e)
  }
  __onRecordBegin(e) {
    console.log(e)
  }
  __heartCheck() {
    clearTimeout(this.timeoutObj)
    this.timeoutObj = setTimeout(() => {
      // 心跳时间内收不到消息，主动触发连接关闭，开始重连
      console.log('onclose')
      if (this._websocket.readyState == 1) {
        this.__close()
      }
    }, this.timeout)
  }
  /**
   * 向后端换一个带签名的评测地址。
   * 三条失败路径都要 reject —— 旧实现只在成功时 resolve，后端报错 / 断网时 Promise
   * 永久悬挂，界面就一直停在「准备录音」且没有任何提示。
   */
  __fetchUrl() {
    return new Promise((resolve, reject) => {
      utilV2.getData(
        ApiPath.xf_evaluation_service,
        {
          engine: 'cn_see',
          result_type: 'xml_all',
        },
        (res) => {
          if (res && res.evaluation_url) {
            resolve(res.evaluation_url)
          } else {
            console.error('[ise] 签发接口返回里没有 evaluation_url', res)
            reject(new Error('evaluation_url missing'))
          }
        },
        (response) => {
          // 传了 fail 回调，http.js 就不再弹后端 errmsg 的通用弹框；
          // 改由上层的 error_callback 提示，并把 errmsg 打到控制台便于排查
          console.error(
            '[ise] 签发评测地址失败',
            response && response.errcode,
            response && response.errmsg
          )
          reject(new Error('sign failed'))
        },
        {},
        () => {
          console.error('[ise] 签发评测地址请求失败（网络）')
          reject(new Error('network error'))
        }
      )
    })
  }
}
